import { v4 as uuid } from 'uuid'
import React, { JSX, memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { ChartContext } from '../chart.context'
import { SeriesGroup, SeriesOutput } from '../chart.typings'
import { CandlestickData, PriceFormat } from 'lightweight-charts'
import { SeriesGroupCategory } from '../../../pages/home'
import { blue, green, red } from '../../../assets/colors'
import './seriesMetadataDisplay.css'
import { useDispatch, useSelector } from 'react-redux'
import { selectChartConfigSettings, selectChartPanes, selectChartPanesConfig, selectChartSeries, selectChartTradingSettings, selectIndicator } from '../../../selectors/chart'
import { Avatar, Box, Button, ClickAwayListener, Collapse, IconButton, Stack, SvgIcon, Typography, useTheme } from '@mui/material'
import Popover from '@mui/material/Popover'
import SeriesGroupConfigurationPopup from './seriesGroupConfigPopup/seriesGroupConfigurationPopup'
import { ChartMetadataContext } from '../chartMetadata/chartMetadata'
import { formatLargeNumber } from '../../../util/screener'
import SeriesGroupMenu from './seriesGroupMenu/seriesGroupMenu'
import { ExtraSmallButton, NonInteractiveTooltip, UltraSmallButton } from '../../custom'
import TimeBuckets from '../../timeBuckets/TimeBuckets'
import SettingsOutlinedIcon from '../../../assets/icons/SettingOutlined'
import { LOGOS_BASE_URL } from '../../../assets/constants'
import { calculateChange, getColorForSymbol, mapIndicatorInputTypeToDrawingSettingType, toLocaleStringIn } from '../../../util/general'
import ChartDrawingSettingsModal from '../chartDrawingSettings/chartDrawingSettingsModal'
import { openConnectAccountDialog, setQuickOrderData, updateOrderHistory } from '../../../reducer/orders.reducer'
import { ChartEvents } from '../chart.events'
import { DrawingSettingGroup } from '../drawingTools/drawing.interface'
import { SettingType } from '../drawingTools/drawing.enums'
import { selectReferenceSecurityMap, selectSnapshot } from '../../../selectors/security'
import { Security, SecurityCategory_CategoryCode } from '../../../proto/generated/security'
import { Broker_OrderType, Broker_Variety, Order, OrderStatus, OrderType, Product, TransactionType, Validity } from '../../../proto/generated/order'
import { setSecuritySearchOpen } from '../../../reducer/home.reducer'
import OptionsChartStrikeMenu from '../../../pages/home/optionsChartHeader/optionsChartStrikeMenu/optionsChartStrikeMenu'
import OptionsChartExpiryMenu from '../../../pages/home/optionsChartHeader/optionsChartExpiryMenu/optionsChartExpiryMenu'
import FutureExpiryMenu from '../../../pages/home/futuresHeader/futureExpiryMenu'
import { getSecurityAsync, getSecurityKey } from '../../../util/security'
import { extractColorFromDataPoint } from '../../../util/chart'
import Hide from '../../../assets/icons/Hide'
import Show from '../../../assets/icons/Show'
import { IndicatorInput_InputType, IndicatorOutput_OutputType } from '../../../proto/generated/indicator'
import { selectPrimarySecurity } from '../../../selectors/home'
import BinIcon from '../../../assets/icons/BinIcon'
import ErrorIcon from '@mui/icons-material/Error'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { convertExpressionToDisplayExpression, getEmbeddedStrategyExpressionLegDisplayStrings, normalizeExpressionKeyFromUrl } from '../../../util/expression'
import ExchangeMenuButton from '../exchangeMenuButton/ExchangeMenuButton'
import { setTradeOptions, TradeOptions } from '../../../reducer/scalper.reducer'
import FlashIcon from '../../../assets/icons/Flash'
import NumberInputField from '../../common/numberInputField/numberInputField'
import { selectPrimaryTradingAccount, selectTradingAccounts } from '../../../selectors/user'
import { PlaceOrModifyOrdersRequest, TradingAccountStatus } from '../../../proto/generated/trading-bridge'
import { SurveillanceWarningDialog } from '../../quickTradeWindow/RiskDisclaimationDialog'
import { placeOrModifyOrder } from '../../../services/apis/orders'
import { addOrderAlertMessage } from '../../../util/orders'
import { selectIsEmbedded } from '../../../selectors/device.ts'
import { isEmbeddedStrategyChartContext } from '../../../util/embeddedChartContext'
import { callNative } from '../../../util/nativeBridge'
import { isDatePassed } from '../../../util/tradepanel.ts'
import AlarmIcon from '../../../assets/icons/AlarmIcon'
import { selectUserAlerts } from '../../../selectors/alerts'
import { AlertExpression_Type, UserAlert_Status, UserAlert_Type } from '../../../proto/generated/nexus'
import { getPrimarySecurityAlertCondition, getUserAlertUnderlyingSecurity, isUserAlertVolumeLevelThreshold } from '../../../util/alerts'
import { sanitizeId } from '../../../util/indicators'

export type SeriesMetadataProps = {
    seriesGroup: SeriesGroup
    primaryChart: boolean
    screenerChart: boolean
    tradeOptions?: TradeOptions
    onSecuritySelected?: (key: string, expressionMode: boolean, securities: string[]) => void
    view: any
}
const SeriesMetadataDisplay: React.FC<SeriesMetadataProps> = ({ seriesGroup, screenerChart, primaryChart, tradeOptions, onSecuritySelected }) => {
    const dispatch = useDispatch()
    const theme = useTheme()
    const chartContext = useContext(ChartContext)
    const indicator = useSelector(selectIndicator(seriesGroup.scriptCode))
    const metadataContext = useContext(ChartMetadataContext)
    const refSecurityMap = useSelector(selectReferenceSecurityMap)
    const { expressionMode, key, expressionSecurities } = useSelector(selectPrimarySecurity)
    const { id, category, primary, series } = seriesGroup
    const showLogo = useMemo(() => {
        return primaryChart || screenerChart
    }, [primaryChart, screenerChart])
    const { viewKey } = chartContext!
    const [open, setOpen] = useState(false)
    const [openMore, setOpenMore] = useState(false)
    const [openMoreTooltip, setOpenMoreTooltip] = useState(false)
    let elements: React.ReactNode
    const [visible, toggleVisible] = useState(true)
    const panes = useSelector(selectChartPanes(viewKey))
    const panesConfig = useSelector(selectChartPanesConfig(viewKey))
    const seriesConfig = useSelector(selectChartSeries(viewKey))
    const [active, setActive] = useState<boolean>(false)
    const [openDrawingSettings, setOpenDrawingSettings] = useState<boolean>(false)
    const { chart } = chartContext!
    const [drawingIds, setDrawingIds] = useState<string[]>([])
    const chartSecurities = useMemo(() => chartContext?.chart?.securities, [chartContext?.chart?.securities])
    const chartExpressionMode = useMemo(() => chartContext?.chart?.expression, [chartContext?.chart?.expression])
    const metaData = metadataContext[id]
    const [showCumulative, setShowCumulative] = useState<boolean>(false)
    const chartSecurityKey = chartExpressionMode || !chartSecurities?.length ? '' : getSecurityKey(chartSecurities?.[0]?.exchange, chartSecurities?.[0]?.code)
    const [security, setSecurity] = useState<Security | undefined>()
    const [tradedSecurity, setTradedSecurity] = useState<Security | undefined>()

    useEffect(() => {
        if (!chartSecurityKey) return
        getSecurityAsync(chartSecurityKey).then((sec) => {
            setSecurity(sec)
            if (!sec) return
            if (sec.derived) {
                getSecurityAsync(refSecurityMap[getSecurityKey(sec.exchange, sec.code)]).then(setTradedSecurity)
            } else {
                setTradedSecurity(sec)
            }
        })
    }, [chartSecurityKey, refSecurityMap])

    const chartSettingsStatusLine = useSelector(selectChartConfigSettings(viewKey))
    const chartTradingSettings = useSelector(selectChartTradingSettings(viewKey))
    const snapshot = useSelector(selectSnapshot(chartSecurityKey))
    const tradingAccounts = useSelector(selectTradingAccounts)
    const selectedTradingAccount = useSelector(selectPrimaryTradingAccount)
    const [showSurveillanceDialog, setShowSurveillanceDialog] = useState(false)
    const [embedStrategyLegsExpanded, setEmbedStrategyLegsExpanded] = useState(false)
    const [ltpChange, setLTPChange] = useState<number>(0)
    const [ltpChangePercent, setLTPChangePercent] = useState<number>(0)
    const isEmbedded = useSelector(selectIsEmbedded)
    const userAlerts = useSelector(selectUserAlerts)
    // Check if we're in scalper embedded mode by checking tradeOptions
    const isScalperEmbedded = useMemo(() => {
        return isEmbedded && !!tradeOptions
    }, [isEmbedded, tradeOptions])
    const showOptionStrikesMenu = useMemo(() => {
        return !!(!chartExpressionMode && chartSecurities?.length && primary && chartSecurities[0].categoryCode === SecurityCategory_CategoryCode.OPTIONS)
    }, [chartExpressionMode, chartSecurities, primary])

    const placeOrderRequestRef = useRef<PlaceOrModifyOrdersRequest>(null)

    const surveillanceInfo = useMemo(() => {
        const code = Number(tradedSecurity?.surveillanceIndicator)
        return {
            symbol: tradedSecurity?.code ?? '',
            warningTitle: 'Regulatory Caution',
            cautions: code ? [String(code)] : []
        }
    }, [tradedSecurity])

    const securityValid = useMemo(() => {
        if (security?.expiry?.expiry) {
            return isDatePassed(security.expiry.expiry) ? false : true
        }
        return true
    }, [security])

    useEffect(() => {
        if (snapshot && (chartSettingsStatusLine?.showLastDayChange || isEmbedded)) {
            const { change, changePercentage } = calculateChange(snapshot.lastTradedPrice, snapshot.previousClose)
            setLTPChange(change)
            setLTPChangePercent(changePercentage)
        }
    }, [snapshot, isEmbedded, chartSettingsStatusLine?.showLastDayChange])

    useEffect(() => {
        if (seriesGroup.name === 'OI Profile') {
            setShowCumulative((seriesGroup.outputValues[seriesGroup.series[0]]?.drawingSettings?.settings?.cumulative ?? false) as boolean)
        }
    }, [seriesGroup])

    useEffect(() => {
        setEmbedStrategyLegsExpanded(false)
    }, [key])

    const disableInput = useMemo(() => {
        return indicator?.name === 'Open Interest Outlook' && ![SecurityCategory_CategoryCode.STOCKS, SecurityCategory_CategoryCode.INDEX].includes(security?.categoryCode as any)
    }, [security?.categoryCode, indicator?.name])

    const showOptionExpiriesMenu = useMemo(() => {
        return !!(!chartExpressionMode && chartSecurities?.length && primary && chartSecurities[0].categoryCode === SecurityCategory_CategoryCode.OPTIONS)
    }, [chartExpressionMode, chartSecurities, primary])

    const showFutureExpiriesMenu = useMemo(() => {
        return !!(!chartExpressionMode && chartSecurities?.length && primary && chartSecurities[0].categoryCode === SecurityCategory_CategoryCode.FUTURES)
    }, [chartExpressionMode, chartSecurities, primary])

    const [underlyingName, setUnderlyingName] = useState('')
    useEffect(() => {
        if (chartExpressionMode || !chartSecurities?.length) {
            setUnderlyingName('')
            return
        }
        if (chartSecurities[0].hasFo) {
            setUnderlyingName(chartSecurities[0].symbol ?? '')
            return
        }
        const underlyingKey = chartSecurities[0].underlyingSecurityCode
            ? getSecurityKey(chartSecurities[0].exchange, chartSecurities[0].underlyingSecurityCode)
            : null
        if (!underlyingKey) {
            setUnderlyingName('')
            return
        }
        getSecurityAsync(underlyingKey).then((sec) => setUnderlyingName(sec?.symbol ?? ''))
    }, [chartExpressionMode, chartSecurities])

    const derived = useMemo(() => !chartExpressionMode && chartSecurities?.length && chartSecurities[0].derived, [chartExpressionMode, chartSecurities])
    const [alertsAnchorEl, setAlertsAnchorEl] = useState<HTMLElement | null>(null)

    const primarySecurityKey = useMemo(() => {
        const sec = chartSecurities?.[0]
        if (!sec || chartExpressionMode) return ''
        return getSecurityKey(sec.exchange, sec.code)
    }, [chartSecurities, chartExpressionMode])

    const normalizeIndicatorInputs = useCallback((values: Record<string, string>) => {
        const normalized: Record<string, string> = {}
        Object.entries(values || {}).forEach(([k, v]) => {
            normalized[sanitizeId(k)] = String(v ?? '')
        })
        return normalized
    }, [])

    const indicatorAlertsForSeriesGroup = useMemo(() => {
        if (!primarySecurityKey) return []
        if (primary || category === SeriesGroupCategory.SERIES_COMPARABLE_SECURITY || category === SeriesGroupCategory.SERIES_DRAWING) return []
        if (category === SeriesGroupCategory.SERIES_VOLUME) {
            return userAlerts.filter((alert) => {
                if (alert.status !== UserAlert_Status.ACTIVE) return false
                if (alert.type === UserAlert_Type.SCREENER) return false
                if (!isUserAlertVolumeLevelThreshold(alert)) return false
                const topLevelKey = alert.security?.code ? getSecurityKey(alert.security.exchange, alert.security.code) : ''
                const [underlying] = getUserAlertUnderlyingSecurity(alert)
                const underlyingKey = underlying?.[0]?.code ? getSecurityKey(underlying[0].exchange, underlying[0].code) : ''
                return topLevelKey === primarySecurityKey || underlyingKey === primarySecurityKey
            })
        }
        const outputValues = new Set<string>()
        seriesGroup.series.forEach((sid) => {
            const conf = seriesConfig[sid]
            if (!conf) return
            if (conf.code) outputValues.add(String(conf.code))
            if (conf.title) outputValues.add(String(conf.title))
            if (conf.name) outputValues.add(String(conf.name))
        })
        if (!outputValues.size) return []
        const normalizedInputs = normalizeIndicatorInputs(seriesGroup.inputValues)
        return userAlerts.filter((alert) => {
            if (alert.status !== UserAlert_Status.ACTIVE) return false
            if (alert.type === UserAlert_Type.SCREENER) return false
            const topLevelKey = alert.security?.code ? getSecurityKey(alert.security.exchange, alert.security.code) : ''
            const [underlying] = getUserAlertUnderlyingSecurity(alert)
            const underlyingKey = underlying?.[0]?.code ? getSecurityKey(underlying[0].exchange, underlying[0].code) : ''
            if (topLevelKey !== primarySecurityKey && underlyingKey !== primarySecurityKey) return false
            const sac = getPrimarySecurityAlertCondition(alert.condition)
            const lhs = sac?.lhsExpression
            if (!lhs || lhs.type !== AlertExpression_Type.TECHNICAL_INDICATOR || !lhs.indicator) return false
            if (lhs.indicator.indicatorCode !== seriesGroup.scriptCode) return false
            if (!outputValues.has(String(lhs.indicator.output))) return false
            const alertInputs: Record<string, string> = {}
            ;(lhs.indicator.params || []).forEach((p) => {
                if (!p?.key) return
                alertInputs[sanitizeId(p.key)] = String(p.value ?? '')
            })
            return Object.keys(alertInputs).every((k) => (normalizedInputs[k] ?? '') === (alertInputs[k] ?? ''))
        })
    }, [primarySecurityKey, primary, category, seriesGroup, seriesConfig, userAlerts, normalizeIndicatorInputs])

    const comparableAlertsForSeriesGroup = useMemo(() => {
        if (!primarySecurityKey) return []
        if (category !== SeriesGroupCategory.SERIES_COMPARABLE_SECURITY) return []
        const compareSecurityKey = seriesGroup.securityList?.[0]
        if (!compareSecurityKey) return []
        return userAlerts.filter((alert) => {
            if (alert.status !== UserAlert_Status.ACTIVE) return false
            if (alert.type === UserAlert_Type.SCREENER) return false
            const topLevelKey = alert.security?.code ? getSecurityKey(alert.security.exchange, alert.security.code) : ''
            if (topLevelKey !== primarySecurityKey) return false
            const sac = getPrimarySecurityAlertCondition(alert.condition)
            const lhs = sac?.lhsExpression
            if (!lhs || lhs.type !== AlertExpression_Type.SIMPLE_SECURITY || !lhs.simpleSecurity?.security) return false
            const lhsKey = getSecurityKey(lhs.simpleSecurity.security.exchange, lhs.simpleSecurity.security.code)
            return lhsKey === compareSecurityKey
        })
    }, [primarySecurityKey, category, seriesGroup.securityList, userAlerts])

    const groupedAlerts = useMemo(() => {
        return category === SeriesGroupCategory.SERIES_COMPARABLE_SECURITY ? comparableAlertsForSeriesGroup : indicatorAlertsForSeriesGroup
    }, [category, comparableAlertsForSeriesGroup, indicatorAlertsForSeriesGroup])

    const alertBadge = useMemo(() => {
        if (!groupedAlerts.length || screenerChart || isScalperEmbedded) return null
        return (
            <NonInteractiveTooltip placement="top" title={`Alerts · ${groupedAlerts.length}`} arrow>
                <Box
                    onClick={(e) => {
                        e.stopPropagation()
                        setAlertsAnchorEl(e.currentTarget as HTMLElement)
                    }}
                    sx={{
                        height: '24px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        color: '#25B39E'
                    }}
                >
                    <AlarmIcon style={{ fontSize: '24px' }} />
                </Box>
            </NonInteractiveTooltip>
        )
    }, [groupedAlerts, screenerChart, isScalperEmbedded])

    const derivedDetails: any = useMemo(() => {
        if (!primary || !derived) return undefined
    }, [primary, derived, metaData, showOptionExpiriesMenu, chartSecurities])

    const securityDisplayName = useMemo(() => {
        if (chartExpressionMode) return seriesGroup.name
        if (category === SeriesGroupCategory.SERIES_COMPARABLE_SECURITY) return seriesGroup.name
        const currentSecurity = chartSecurities[0]
        if (currentSecurity && currentSecurity.hasFo) return seriesGroup.name
        return underlyingName
    }, [chartExpressionMode, category, chartSecurities, seriesGroup.name, underlyingName])

    let name: JSX.Element = primary || category === SeriesGroupCategory.SERIES_COMPARABLE_SECURITY ? <span>{securityDisplayName}</span> : <span></span>
    let expressionName: string = primary || category === SeriesGroupCategory.SERIES_COMPARABLE_SECURITY ? securityDisplayName : ''

    if (screenerChart) {
        name = <span>{seriesGroup.name}</span>
        expressionName = seriesGroup.name
    }

    const handlePlaceOrder = useCallback(async (request: PlaceOrModifyOrdersRequest) => {
        try {
            const created = Date.now()
            const response = await placeOrModifyOrder(request)
            const order = response.orderResponse.length ? response.orderResponse[0] : null
            if (!order) throw new Error()
            const { successful, commitOrder, brokerOrderId, orderId, errorMessage } = order
            if (!successful) {
                addOrderAlertMessage({
                    ...request.order[0],
                    status: OrderStatus.FAILED,
                    message: errorMessage || 'Unable to place order'
                })
            } else {
                const updateOrder: Order = {
                    ...request.order[0],
                    brokerOrderId,
                    status: OrderStatus.OPEN,
                    filledQuantity: 0,
                    orderId,
                    created
                }
                if (commitOrder) {
                    dispatch(updateOrderHistory({ updates: [updateOrder] }))
                }
                addOrderAlertMessage({
                    ...updateOrder,
                    message: 'Order placed successfully'
                })
            }
        } catch (error: any) {
            addOrderAlertMessage({
                ...request.order[0],
                status: OrderStatus.FAILED,
                message: error.message || 'Unable to place order'
            })
        }
    }, [])
    const handleQuickTrade = useCallback(
        (
            transaction: TransactionType,
            orderType: OrderType = OrderType.NORMAL,
            brokerOrderType: Broker_OrderType = Broker_OrderType.LIMIT_ORDER,
            product: Product = Product.INTRADAY,
            price: number | undefined = undefined
        ) => {
            let quantity = tradedSecurity?.lotSize ?? 1
            let finalOrderType = brokerOrderType
            let finalProduct = product

            if (!tradedSecurity) return

            if (tradeOptions) {
                const { quantityMultiplier, orderType: tradeOrderType, orderProduct } = tradeOptions
                quantity = (tradedSecurity.lotSize ?? 1) * (quantityMultiplier ?? 1)
                finalOrderType = Number(tradeOrderType) ?? brokerOrderType
                finalProduct = orderProduct ?? product
            }

            const intraday = Number(finalProduct) === Product.INTRADAY

            if (!intraday) {
                if (tradedSecurity.categoryCode === SecurityCategory_CategoryCode.FUTURES || tradedSecurity.categoryCode === SecurityCategory_CategoryCode.OPTIONS) {
                    finalProduct = Product.MARGIN
                } else {
                    finalProduct = Product.CNC
                }
            }

            if (tradeOptions?.quickTrade) {
                const request: PlaceOrModifyOrdersRequest = {
                    buyBeforeSell: false,
                    tradingAccountId: selectedTradingAccount.accountId ?? '',
                    order: [
                        {
                            security: {
                                code: tradedSecurity?.code ?? '',
                                exchange: tradedSecurity?.exchange ?? 0
                            },
                            transactionType: transaction,
                            orderType: OrderType.NORMAL,
                            brokerOrderType: finalOrderType,
                            quantity: Number(quantity),
                            product: finalProduct,
                            price: finalOrderType === Broker_OrderType.MARKET_ORDER ? undefined : price !== undefined ? Math.round(price) : undefined,
                            triggerPrice: 0,
                            brokerVariety: Broker_Variety.REGULAR,
                            validity: Validity.DAY,
                            clientOrderId: uuid(),
                            batchId: uuid(),
                            orderDate: Date.now(),
                            isBasket: false,
                            immediateExecutePaperOrder: false
                        }
                    ]
                }

                if (!selectedTradingAccount || !tradingAccounts || tradingAccounts?.length === 0) {
                    dispatch(
                        openConnectAccountDialog({
                            type: 'connect',
                            tradingAccountId: ''
                        })
                    )
                    return
                } else if (selectedTradingAccount.status !== TradingAccountStatus.ACTIVE) {
                    dispatch(
                        openConnectAccountDialog({
                            type: 'reconnect',
                            tradingAccountId: selectedTradingAccount.accountId
                        })
                    )
                    return
                } else if (tradedSecurity.surveillanceIndicator) {
                    placeOrderRequestRef.current = request
                    setShowSurveillanceDialog(true)
                    return
                }

                handlePlaceOrder(request)
            } else {
                dispatch(
                    setQuickOrderData({
                        openDialog: true,
                        mode: 'regular',
                        type: 'trade',
                        isSquareOffOrder: false,
                        order: {
                            transactionType: transaction,
                            security: tradedSecurity,
                            editOrder: undefined,
                            brokerOrderType: finalOrderType,
                            orderType,
                            product: finalProduct,
                            price: finalOrderType === Broker_OrderType.MARKET_ORDER ? undefined : price,
                            quantity,
                            maxQuantity: 0
                        }
                    })
                )
            }
        },
        [dispatch, tradedSecurity, tradeOptions, selectedTradingAccount, tradingAccounts]
    )

    const displayText = useMemo(() => {
        if (!expressionMode) {
            return security?.symbol || 'Security'
        }
        return convertExpressionToDisplayExpression(key, expressionSecurities)
    }, [expressionMode, expressionSecurities, security, key, chartSecurityKey])

    if (showLogo) {
        const security = chartContext?.chart?.securities?.[0]
        if (security) {
            name = (
                <Stack direction={'row'} spacing={1} alignItems={'center'}>
                    {(chartSettingsStatusLine?.logo ?? true) && showLogo && primary ? (
                        security.logoUrl ? (
                            <Avatar alt={seriesGroup.name} src={`${LOGOS_BASE_URL}${security.logoUrl}.svg`} sx={{ width: 20, height: 20 }} />
                        ) : (
                            <Avatar
                                alt={displayText}
                                sx={{
                                    width: 20,
                                    height: 20,
                                    fontSize: 10,
                                    color: 'white',
                                    bgcolor: getColorForSymbol(security?.symbol || security?.code || '', theme.palette.background.default)
                                }}
                            >
                                {(security?.symbol || security?.code || '')
                                    .split(' ')
                                    .map((word) => word[0]?.toUpperCase())
                                    .slice(0, 2)
                                    .join('')}
                            </Avatar>
                        )
                    ) : null}
                    {screenerChart && onSecuritySelected ? (
                        <ExtraSmallButton
                            variant={"contained"}
                           
                            sx={{
                                height: '24px',
                               display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                padding: 1,
                                minWidth: 'auto',
                                textTransform: 'none',
                                backgroundColor: (theme) => theme.palette.background.default,
                                color:(theme) => theme.palette.text.primary,
                                '&:hover': {
                                    backgroundColor: (theme) => theme.palette.background.default,
                                }
                            }}
                            onClick={(e) => {
                                e.stopPropagation()
                                if (chartSecurities?.[0]) {
                                    const secKey = getSecurityKey(chartSecurities[0].exchange, chartSecurities[0].code)
                                    onSecuritySelected(secKey, false, [secKey])
                                }
                            }}
                        >
                           <Typography fontSize={'13px'}> {name} </Typography>
                        </ExtraSmallButton>
                    ) : (
                        <Typography variant={'body2'} sx={{ height: '24px', display: 'flex', alignItems: 'center' }}>
                            {name}
                        </Typography>
                    )}
                </Stack>
            )
        }
    }
    name = category === SeriesGroupCategory.SERIES_VOLUME ? isScalperEmbedded ? <span></span> : <span>Volume</span> : name
    const [paneId] = useMemo(() => {
        return panes.filter((pid) => {
            const p = panesConfig[pid]
            return p && p.seriesGroups.indexOf(id) !== -1
        })
    }, [id, panes, panesConfig])

    const onRemoveClick = useCallback(() => {
        if (category === SeriesGroupCategory.SERIES_COMPARABLE_SECURITY) {
            chart?.removeComparableSecurity(id)
            return
        }
        if (paneId && panesConfig[paneId].seriesGroups.length === 1) {
            chart.removePane(panes.indexOf(paneId))
        } else {
            chart.removeSeries(id)
        }
    }, [category, id, chart, paneId, panesConfig, panes])
    const handleKeyDown = useCallback(
        (e: any) => {
            const key = e.key
            const hasOpenDialog = !!document.querySelector('[role="dialog"]')
            if (!primary && active && !e.target.matches('input, textarea') && !hasOpenDialog && onRemoveClick && ['backspace', 'delete'].indexOf(key.toLowerCase()) !== -1) {
                onRemoveClick()
            }
        },
        [primary, active, onRemoveClick]
    )
    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown)

        return () => {
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [handleKeyDown])

    const onDrawingDestroyed = useCallback(
        (event: CustomEvent) => {
            const { id: drawingId } = event.detail
            if (chart && id) {
                const dIds = chart.getDrawingIds(id)
                if (dIds.includes(drawingId)) {
                    setDrawingIds(dIds.filter((dId) => dId !== drawingId))
                    setOpenDrawingSettings(false)
                }
            }
        },
        [id, chart]
    )

    useEffect(() => {
        chart?.subscribeEvent(ChartEvents.DRAWING_DESTROYED, onDrawingDestroyed)
        return () => {
            chart?.unsubscribeEvent(ChartEvents.DRAWING_DESTROYED, onDrawingDestroyed)
        }
    }, [chart, onDrawingDestroyed])

    const handleMenuOpen = useCallback(() => {
        setOpenMoreTooltip(false)
        setOpenMore(true)
    }, [])

    const handleMenuClose = useCallback(() => {
        setOpenMoreTooltip(false)
        setOpenMore(false)
    }, [])

    const displayValue = useCallback((value: number, priceFormat: PriceFormat['type']): string => {
        switch (priceFormat) {
            case 'price':
                return toLocaleStringIn(value)
            case 'volume':
                return formatLargeNumber(value)
            case 'percent':
                return `${value.toFixed(2)}%`
            default:
                return value.toFixed(2)
        }
    }, [])

    const [chartWidth, setChartWidth] = useState<number>(0)
    useEffect(() => {
        const handleResize = () => {
            setChartWidth(chart?.container?.offsetWidth || 0)
        }
        handleResize()
        const resizeObserver = new ResizeObserver(handleResize)
        resizeObserver.observe(chart?.container || document.body)
        return () => resizeObserver.disconnect()
    }, [chart])

    const shouldDisplayAllOHLC = useMemo(() => {
        return (chartSettingsStatusLine?.showOHLC ?? true)
    }, [chartWidth, chartSettingsStatusLine])

    if (primary) {
        const [data] = Object.values(metadataContext[id] || {})
        const candleData = data as CandlestickData[]
        let change = 0,
            changePercent = 0
        if (candleData && candleData.length > 1) {
            change = (candleData[1]?.close ?? 0) - (candleData[0]?.close ?? 0)
            changePercent = (change * 100) / (candleData[0]?.close ?? 0)
        }
        const lastCandle = candleData ? candleData[candleData.length - 1] : undefined
        const color = lastCandle && lastCandle.close >= lastCandle.open ? green : red
        const style: React.CSSProperties = {
            color,
            fontSize: '13px'
        }
        const strike = chart?.dataManager?.referenceSecurities?.[(lastCandle as any)?.referencedSecurityCode]?.strikePrice / (chartSecurities?.[0]?.priceMultiplier ?? 100)

        const isCrosshairActive = !!chart?.crossHairInfo
        const showEmbeddedDayView = isEmbedded && !isCrosshairActive
        const forceOHLC = isEmbedded && isCrosshairActive

        elements = showEmbeddedDayView ? (
            <span className="series-ohlc-container" style={{ flexBasis: '100%' }}>
                <span className="series-meta-value" style={{ color: ltpChange > 0 ? green : ltpChange < 0 ? red : color, fontSize: '13px' }}>
                    {toLocaleStringIn(lastCandle?.close || 0)}
                </span>
                <span className="series-meta-value series-change-container" style={{ color: ltpChange > 0 ? green : ltpChange < 0 ? red : color, fontSize: '13px' }}>
                    <span className="series-sign">{ltpChange > 0 ? '+' : ltpChange < 0 ? '-' : ''}</span>
                    {(Math.abs(expressionMode ? ltpChange : ltpChange / 100) || 0).toFixed(2)} (<span className="series-sign">{ltpChangePercent > 0 ? '+' : ltpChangePercent < 0 ? '-' : ''}</span>
                    {(Math.abs(ltpChangePercent) || 0).toFixed(2)}%)
                </span>
            </span>
        ) : (
            <span className="series-ohlc-container">
                {derived && showOptionExpiriesMenu && (
                    <>
                        <span className="series-meta-label">Strike</span>
                        <span className="series-meta-value" style={style}>
                            {strike ?? '-'}
                        </span>
                    </>
                )}
                {(forceOHLC || shouldDisplayAllOHLC) && (
                    <>
                        <span className="series-meta-label">O</span>
                        <span className="series-meta-value" style={style}>
                            {toLocaleStringIn(lastCandle?.open || 0)}
                        </span>
                        <span className="series-meta-label">H</span>
                        <span className="series-meta-value" style={style}>
                            {toLocaleStringIn(lastCandle?.high || 0)}
                        </span>
                        <span className="series-meta-label">L</span>
                        <span className="series-meta-value" style={style}>
                            {toLocaleStringIn(lastCandle?.low || 0)}
                        </span>
                    </>
                )}
                {(forceOHLC || (chartSettingsStatusLine?.showOHLC ?? true)) && (
                    <>
                        <span className="series-meta-label">C</span>
                        <span className="series-meta-value" style={style}>
                            {toLocaleStringIn(lastCandle?.close || 0)}
                        </span>
                    </>
                )}
                {(forceOHLC || (chartSettingsStatusLine?.showBarChange ?? true)) && (
                    <span className="series-meta-value series-change-container" style={style}>
                        <span className="series-sign">{change > 0 ? '+' : change < 0 ? '-' : ''}</span>
                        {(Math.abs(change) || 0).toFixed(2)} (<span className="series-sign">{changePercent > 0 ? '+' : changePercent < 0 ? '-' : ''}</span>
                        {(Math.abs(changePercent) || 0).toFixed(2)}%)
                    </span>
                )}

                {!isEmbedded && chartSettingsStatusLine?.showLastDayChange && (
                    <span style={{ ...style, display: 'flex', alignItems: 'center' }} className={ltpChange > 0 ? 'green-text' : ltpChange < 0 ? 'red-text' : ''}>
                        {`${ltpChange > 0 ? '+' : ''}${(expressionMode ? ltpChange : ltpChange / 100).toFixed(2)} (${ltpChangePercent > 0 ? '+' : ''}${ltpChangePercent.toFixed(2)}%)`}
                    </span>
                )}
            </span>
        )
    } else if (category === SeriesGroupCategory.SERIES_COMPARABLE_SECURITY) {
        const [data] = Object.values(metadataContext[id] || {})
        const candleData = data as CandlestickData[]
        const lastCandle = candleData?.[(candleData?.length ?? 0) - 1] ?? undefined
        const sid = series[0]
        const seriesConf = seriesConfig[sid]
        elements = lastCandle ? (
            <>
                <span className="series-meta-value" style={{ fontSize: '13px', color: seriesConf.color }}>
                    {displayValue(lastCandle?.close || 0, seriesConfig[sid].priceFormat)}
                </span>
            </>
        ) : (
            <></>
        )
    } else {
        name = (
            <>
                <span>{indicator?.shortName}</span>
                {showCumulative && <span> Cumulative</span>}
            </>
        )
        let inputLabels: string[] = []

        if (seriesGroup.inputValues) {
            indicator.inputs.forEach((input) => {
                if (input?.group === indicator.inputs?.[0]?.group) {
                    if (input.type === IndicatorInput_InputType.BOOLEAN) {
                        if (seriesGroup.inputValues[input.code] && seriesGroup.inputValues[input.code] !== 'false' && !disableInput) {
                            inputLabels.push(input.title)
                        }
                    } else {
                        seriesGroup.inputValues[input.code] && inputLabels.push(seriesGroup.inputValues[input.code])
                    }
                }
            })
        }

        if (inputLabels.length && chartSettingsStatusLine?.indicatorInputs) {
            name = (
                <>
                    <span>{indicator?.shortName}</span>
                    {showCumulative && <span> Cumulative</span>}
                    {inputLabels.map((label, index) => (
                        <Typography component={'span'} fontSize={'13px'} sx={{ lineHeight: '24px' }} key={index} color={'text.disabled'} className="series-meta-input-labels">
                            {label}
                        </Typography>
                    ))}
                </>
            )
        }

        elements = seriesGroup.series.map((sid) => {
            const series = seriesConfig[sid]
            const data = metadataContext[id]?.[sid] as any[]
            const [primaryData] = Object.values(metadataContext['primary'] || {}) as CandlestickData[][]

            if (!data || !data.length || !primaryData || !primaryData.length) return <React.Fragment key={sid} />
            const output = seriesGroup?.outputValues?.[sid]
            if (output?.type === IndicatorOutput_OutputType.FILL || output?.type === IndicatorOutput_OutputType.HZ_LINE) {
                return <React.Fragment key={sid} />
            }
            const lastCandle = data[data.length - 1]
            const primaryLastCandle = primaryData[primaryData.length - 1]
            let color = extractColorFromDataPoint(lastCandle, series.chartType) ?? series.color
            if (category === SeriesGroupCategory.SERIES_VOLUME) {
                color = primaryLastCandle && primaryLastCandle.close > primaryLastCandle.open ? green : red
                // Hide volume values in scalper embedded mode
                if (isScalperEmbedded) {
                    return <React.Fragment key={sid} />
                }
            }

            return lastCandle && lastCandle.customValues?.['label'] ? (
                <React.Fragment key={sid}>
                    <span className="series-meta-label">{lastCandle.customValues?.['label'] as string}</span>
                    <div className="series-meta-value" style={{ fontSize: '13px', color }}>
                        {displayValue(lastCandle?.value || 0, series.priceFormat)}
                    </div>
                </React.Fragment>
            ) : lastCandle ? (
                <div className="series-meta-value" style={{ fontSize: '13px', color }} key={sid}>
                    {(chartSettingsStatusLine?.indicatorValues ?? true) && displayValue(lastCandle?.value || 0, series.priceFormat)}
                    {lastCandle?.label ? ` ${lastCandle.label}` : ''}
                </div>
            ) : null
        })
    }

    const handlePopUpClose = () => {
        setOpen(false)
    }

    const onInputsUpdate = (inputValues: Record<string, any>, refresh: boolean = true) => {
        chart.updateSeriesGroupInputs(id, inputValues, refresh)
    }

    const onOutputsUpdate = (outputValues: Record<string, SeriesOutput>) => {
        chart.updateSeriesGroupOutputs(id, outputValues)
    }

    const onInputsAndOutputsUpdate = (inputValues: Record<string, any>, outputValues: Record<string, SeriesOutput>, refresh: boolean = true) => {
        chart.updateSeriesGroupOutputsAndInputs(id, outputValues, inputValues, refresh)
    }

    const extraDrawingSettings =
        indicator?.inputs && Object.keys(indicator.inputs).length > 0
            ? [
                  {
                      id: 'indicatorInputs',
                      displayName: 'Inputs',
                      visible: true,
                      settings: [
                          ...indicator?.inputs?.map((input) => {
                              const type = mapIndicatorInputTypeToDrawingSettingType(input.type)
                              return {
                                  name: input.title,
                                  label: input.title,
                                  type: input.options?.length ? SettingType.SELECT : type,
                                  defaultValue: seriesGroup.inputValues[input.title] ?? input.defval,
                                  custom: true,
                                  visible: true,
                                  range: [Number(input.minval), Number(input.maxval ?? 10)],
                                  options: input.options?.length
                                      ? input.options.map((option) => ({
                                            label: option,
                                            value: option
                                        }))
                                      : []
                              }
                          })
                      ]
                  }
              ]
            : []

    const onDrawingSettingGroupUpdated = (sgs: DrawingSettingGroup<any, any>[]) => {
        const indicatorInputsSettingGroup = sgs?.find((sg) => sg.id === 'indicatorInputs')
        let inputsChange: boolean = false
        let outputsChange: boolean = false
        let inputValues = { ...seriesGroup.inputValues }
        let outputValues = { ...seriesGroup.outputValues }

        //check inputs change
        if (indicatorInputsSettingGroup) {
            const inputs = indicatorInputsSettingGroup.settings.reduce((acc: any, curr) => {
                acc[curr.name] = curr.defaultValue.toString()
                return acc
            }, {})
            Object.keys(seriesGroup.inputValues).forEach((key) => {
                if (inputsChange) {
                    return
                }
                if (inputs[key].toString() !== seriesGroup.inputValues[key].toString()) {
                    inputsChange = true
                    return
                }
            })
            if (inputsChange) {
                inputValues = inputs
            }
        }

        //check outputs change
        const dIds = chart.getDrawingIds(id)
        dIds.forEach((drawingId) => {
            const availableStyleSettings = chart.drawingManager.getDrawingSettings(drawingId).find((s) => s.id === 'style') as DrawingSettingGroup<any, any>
            const settingsValues = chart.drawingManager.getSettingsValues(drawingId)
            const seriesId = chart.getSeriesIdOfDrawingId(id, drawingId)
            if (!seriesId) {
                return
            }
            const newSeriesDrawingSettings = { ...(seriesGroup.outputValues[seriesId]?.drawingSettings?.settings ?? {}) }

            let changed: boolean = false
            Object.keys(newSeriesDrawingSettings).forEach((key) => {
                const type = availableStyleSettings.settings?.find((s) => s.name === key)?.type ?? undefined
                switch (type) {
                    case SettingType.BOOLEAN: {
                        if (newSeriesDrawingSettings[key].toString() !== settingsValues[key].toString()) {
                            changed = true
                            newSeriesDrawingSettings[key] = settingsValues[key]
                        }
                        break
                    }
                    case SettingType.COLOR: {
                        if (newSeriesDrawingSettings[key].toString() !== settingsValues[key].color.toString()) {
                            newSeriesDrawingSettings[key] = settingsValues[key].color
                            changed = true
                        }
                        break
                    }
                    case SettingType.SELECT: {
                        if (newSeriesDrawingSettings[key].toString() !== settingsValues[key].toString()) {
                            newSeriesDrawingSettings[key] = settingsValues[key]
                            changed = true
                        }
                        break
                    }
                }
            })

            if (changed) {
                outputsChange = true
                outputValues = {
                    ...outputValues,
                    [seriesId]: {
                        ...seriesGroup.outputValues[seriesId],
                        drawingSettings: {
                            settings: { ...newSeriesDrawingSettings }
                        }
                    }
                }
            }
        })

        if (inputsChange && outputsChange) {
            onInputsAndOutputsUpdate(inputValues, outputValues)
        } else if (inputsChange) {
            onInputsUpdate(inputValues)
        } else if (outputsChange) {
            onOutputsUpdate(outputValues)
        }
    }

    const visibilityButtonClick = useCallback(
        (e: any) => {
            const result = chart.toggleVisibility(id, !visible)
            if (result) {
                toggleVisible(!visible)
            }
            e.stopPropagation()
        },
        [chart, visible, id]
    )

    const onClickAway = useCallback(() => {
        active && setActive(false)
    }, [active])

    const hideShowButtons = useMemo(() => {
        return (
            !screenerChart && (
                <NonInteractiveTooltip placement={'top'} title={visible ? 'Hide' : 'Show'} arrow>
                    <UltraSmallButton
                        sx={{ maxHeight: '24px', borderRadius: '2px' }}
                        color={'inherit'}
                        disabled={category === SeriesGroupCategory.SERIES_DRAWING}
                        onClick={visibilityButtonClick}
                        data-event={`SeriesMetadata Series ${visible ? 'Hidden' : 'Shown'}`}
                        data-event-series-name={seriesGroup.name}
                    >
                        {visible ? <Show sx={{ fontSize: '1rem' }} /> : <Hide sx={{ fontSize: '1rem' }} />}
                    </UltraSmallButton>
                </NonInteractiveTooltip>
            )
        )
    }, [seriesGroup, visible, screenerChart, category, visibilityButtonClick])

    const settingsButton = useMemo(() => {
        if (screenerChart) return null

        const hasInputsOrOutputs = indicator?.inputs?.length || indicator?.outputs?.length
        const shouldShowButton = category === SeriesGroupCategory.SERIES_DRAWING || hasInputsOrOutputs

        if (!shouldShowButton) return null

        const handleClick = () => {
            if (isEmbedded) {
                if (category === SeriesGroupCategory.SERIES_DRAWING) {
                    if (chart && id) {
                        const drawingIds = chart.getDrawingIds(id)
                        const settingsGroups = [...chart.getDrawingSettings(drawingIds[0]), ...extraDrawingSettings]
                        callNative('onDrawingSettingsClicked', id, drawingIds[0], JSON.stringify(settingsGroups))
                        return
                    }
                } else {
                    callNative('onSeriesMetaDataSettingsClicked', seriesGroup.id)
                    return
                }
            } else {
                if (category === SeriesGroupCategory.SERIES_DRAWING) {
                    if (chart && id) {
                        setOpenDrawingSettings(true)
                        setDrawingIds(chart.getDrawingIds(id))
                    }
                } else {
                    setOpen(true)
                }
            }
        }

        return (
            <NonInteractiveTooltip placement="top" title="Settings" arrow>
                <UltraSmallButton
                    sx={{ maxHeight: '24px', borderRadius: '2px' }}
                    color="inherit"
                    size="small"
                    onClick={handleClick}
                    data-event="SeriesMetadata Settings Clicked"
                    data-event-series-name={seriesGroup.name}
                >
                    <SettingsOutlinedIcon sx={{ fontSize: '18px' }} color="inherit" />
                </UltraSmallButton>
            </NonInteractiveTooltip>
        )
    }, [seriesGroup, screenerChart, category, indicator, chart, id, isEmbedded])

    const closeButton = useMemo(() => {
        return (
            !screenerChart &&
            !primary && (
                <NonInteractiveTooltip placement={'top'} title="Remove" arrow>
                    <UltraSmallButton
                        sx={{ maxHeight: '24px', borderRadius: '2px' }}
                        color="inherit"
                        size="small"
                        onClick={onRemoveClick}
                        data-event={'SeriesMetadata Series Removed'}
                        data-event-series-name={seriesGroup.name}
                    >
                        <BinIcon sx={{ fontSize: '1rem' }} color={'inherit'} />
                    </UltraSmallButton>
                </NonInteractiveTooltip>
            )
        )
    }, [seriesGroup, onRemoveClick, screenerChart, primary])

    const seriesGroupMenu = useMemo(() => {
        return (
            !screenerChart && (
                <NonInteractiveTooltip placement={'top'} title="More" arrow open={openMoreTooltip} onOpen={() => !openMore && setOpenMoreTooltip(true)} onClose={() => setOpenMoreTooltip(false)}>
                    <div style={{ height: '100%' }}>
                        <SeriesGroupMenu seriesGroup={seriesGroup} disabled={!securityValid} onClosed={handleMenuClose} onOpened={handleMenuOpen} />
                    </div>
                </NonInteractiveTooltip>
            )
        )
    }, [seriesGroup, handleMenuClose, handleMenuOpen, openMoreTooltip, openMore, screenerChart, securityValid])

    const strategyEmbeddedLegLabels = useMemo(() => {
        if (!isEmbeddedStrategyChartContext() || !primary || screenerChart) {
            return null
        }
        if (chartContext?.chart?.mode !== 'EXPRESSION') {
            return null
        }
        const rawExpr = chartContext.chart.expression?.trim() ?? ''
        const expr = normalizeExpressionKeyFromUrl(rawExpr).trim()
        if (!expr) {
            return null
        }
        const securities = chartContext.chart.securities ?? []
        const legs = getEmbeddedStrategyExpressionLegDisplayStrings(expr, securities)
        return legs.length > 1 ? legs : null
    }, [primary, screenerChart, chartContext?.chart?.mode, chartContext?.chart?.expression, chartContext?.chart?.securities])

    const nameContainer = useMemo(() => {
        if (strategyEmbeddedLegLabels) {
            const [firstLeg, ...otherLegs] = strategyEmbeddedLegLabels
            return (
                <Box
                    className="series-meta-embedded-multi-leg"
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        alignSelf: 'stretch',
                        minWidth: 0,
                        width: '100%',
                        maxWidth: '100%',
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            flexWrap: 'nowrap',
                            gap: 0.25,
                            minWidth: 0,
                            maxWidth: '100%',
                        }}
                    >
                        <NonInteractiveTooltip placement={'top'} title="Change symbol" arrow>
                            <UltraSmallButton
                                size={'small'}
                                variant="text"
                                color="inherit"
                                sx={{
                                    padding: 0,
                                    margin: 0,
                                    display: 'inline-flex',
                                    minWidth: 0,
                                    maxHeight: 'none',
                                    borderRadius: '2px',
                                    flex: '1 1 auto',
                                    textAlign: 'left',
                                    justifyContent: 'flex-start',
                                }}
                                onClick={() => {
                                    const isExpression = seriesGroup.securityList.length > 1
                                    dispatch(
                                        setSecuritySearchOpen({
                                            open: true,
                                            key: isExpression ? (expressionName as string) : seriesGroup.securityList[0],
                                            isExpression: isExpression,
                                            onSecuritySelected: onSecuritySelected,
                                        }),
                                    )
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        lineHeight: 1.25,
                                        textAlign: 'left',
                                        wordBreak: 'break-word',
                                        overflowWrap: 'anywhere',
                                    }}
                                >
                                    {firstLeg}
                                </Typography>
                            </UltraSmallButton>
                        </NonInteractiveTooltip>
                        <IconButton
                            size="small"
                            sx={{ flexShrink: 0, padding: '2px' }}
                            onClick={(e) => {
                                e.stopPropagation()
                                setEmbedStrategyLegsExpanded((v) => !v)
                            }}
                            aria-label={embedStrategyLegsExpanded ? 'Hide legs' : 'Show all legs'}
                        >
                            {embedStrategyLegsExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                        </IconButton>
                    </Box>
                    <Collapse in={embedStrategyLegsExpanded} timeout="auto" unmountOnExit sx={{ width: '100%' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, pt: 0.5, pl: 0.5, width: '100%' }}>
                            {otherLegs.map((leg, i) => (
                                <Typography
                                    key={`${leg}-${i}`}
                                    variant="body2"
                                    sx={{
                                        lineHeight: 1.25,
                                        color: 'text.secondary',
                                        wordBreak: 'break-word',
                                        overflowWrap: 'anywhere',
                                    }}
                                >
                                    {leg}
                                </Typography>
                            ))}
                        </Box>
                    </Collapse>
                </Box>
            )
        }
        return (
            <NonInteractiveTooltip placement={'top'} title="Change symbol" arrow>
                <UltraSmallButton
                    size={'small'}
                    variant="text"
                    color="inherit"
                    sx={{ padding: 0, margin: 0, display: 'inline-block', maxHeight: '24px', borderRadius: '2px' }}
                    onClick={() => {
                        const isExpression = seriesGroup.securityList.length > 1
                        dispatch(
                            setSecuritySearchOpen({
                                open: true,
                                key: isExpression ? (expressionName as string) : seriesGroup.securityList[0],
                                isExpression: isExpression,
                                onSecuritySelected: onSecuritySelected
                            })
                        )
                    }}
                >
                    {name}
                </UltraSmallButton>
            </NonInteractiveTooltip>
        )
    }, [
        strategyEmbeddedLegLabels,
        embedStrategyLegsExpanded,
        name,
        expressionName,
        seriesGroup.securityList,
        dispatch,
        onSecuritySelected,
    ])

    const seriesGroupError = chartContext?.seriesGroupErrors?.[seriesGroup.id]
    const errorContainer = seriesGroupError ? (
        <NonInteractiveTooltip title={seriesGroupError} arrow placement="top">
            <span>
                <UltraSmallButton sx={{ maxHeight: '24px', color: 'red', cursor: 'default' }} color="inherit" disabled disableRipple disableFocusRipple>
                    <ErrorIcon sx={{ fontSize: '1rem', color: '#cc2f3c88' }} />
                </UltraSmallButton>
            </span>
        </NonInteractiveTooltip>
    ) : null

    const sellMarketContainer = useMemo(() => {
        return (
            <NonInteractiveTooltip title="Sell Market" arrow placement="top">
                {(() => {
                    const lastCandleValues = chart.dataManager.data[chartSecurityKey]?.slice(-1)?.[0]
                    const topBid = lastCandleValues?.topBid ?? 0
                    const flashIcon = tradeOptions?.quickTrade ? (
                        <SvgIcon fontSize="small" sx={{ width: '14px', marginRight: '4px' }}>
                            <FlashIcon />
                        </SvgIcon>
                    ) : null
                    return (
                        <Button
                            variant="outlined"
                            sx={{
                                py: 0,
                                px: 0.5,
                                borderColor: (theme) => theme.palette.error.main,
                                background: (theme) => theme.palette.background.paper,
                                color: (theme) => theme.palette.error.main,
                                '&:hover': {
                                    borderColor: (theme) => theme.palette.error.main
                                }
                            }}
                            onClick={() => {
                                if (isEmbedded) {
                                    callNative('onChartBidAskClicked', 'false', topBid ? (topBid / (chartSecurities?.[0]?.priceMultiplier ?? 100)).toString() : '0')
                                    return
                                }
                                handleQuickTrade(TransactionType.SELL, OrderType.NORMAL, Broker_OrderType.LIMIT_ORDER, Product.INTRADAY, topBid)
                            }}
                        >
                            <span style={{ marginRight: 0 }}>
                                {flashIcon}
                                {toLocaleStringIn(topBid / (chartSecurities?.[0]?.priceMultiplier ?? 100))}
                            </span>
                        </Button>
                    )
                })()}
            </NonInteractiveTooltip>
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chart, handleQuickTrade, isEmbedded, tradeOptions, chartSecurityKey, chartSecurities, chart.dataManager.data[chartSecurityKey]?.slice(-1)?.[0]])

    const spreadContainer = useMemo(() => {
        return (
            <NonInteractiveTooltip title="Spread" arrow placement="top">
                <span className="series-meta-value" style={{ margin: '0 5px' }}>
                    {(() => {
                        const lastCandleValues = chart.dataManager.data[chartSecurityKey]?.slice(-1)?.[0]
                        const topAsk = (lastCandleValues?.topAsk ?? 0) / (chartSecurities?.[0]?.priceMultiplier ?? 100)
                        const topBid = (lastCandleValues?.topBid ?? 0) / (chartSecurities?.[0]?.priceMultiplier ?? 100)
                        return toLocaleStringIn(Math.abs(topAsk - topBid))
                    })()}
                </span>
            </NonInteractiveTooltip>
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chart, chartSecurityKey, chartSecurities, chart.dataManager.data[chartSecurityKey]?.slice(-1)?.[0]])

    const buyMarketContainer = useMemo(() => {
        return (
            <NonInteractiveTooltip title="Buy Market" arrow placement="top">
                {(() => {
                    const lastCandleValues = chart.dataManager.data[chartSecurityKey]?.slice(-1)?.[0]
                    const topAsk = lastCandleValues?.topAsk ?? 0
                    const flashIcon = tradeOptions?.quickTrade ? (
                        <SvgIcon fontSize="small" sx={{ width: '14px', marginRight: '4px' }}>
                            <FlashIcon />
                        </SvgIcon>
                    ) : null
                    return (
                        <Button
                            variant="outlined"
                            sx={{
                                py: 0,
                                px: 0.5,
                                color: blue,
                                borderColor: blue,
                                background: (theme) => theme.palette.background.paper,
                                '&:hover': {
                                    borderColor: blue
                                }
                            }}
                            onClick={() => {
                                if (isEmbedded) {
                                    callNative('onChartBidAskClicked', 'true', topAsk ? (topAsk / (chartSecurities?.[0]?.priceMultiplier ?? 100)).toString() : '0')
                                    return
                                }
                                handleQuickTrade(TransactionType.BUY, OrderType.NORMAL, Broker_OrderType.LIMIT_ORDER, Product.INTRADAY, topAsk)
                            }}
                        >
                            <span style={{ marginRight: 0, lineHeight: '22px' }}>
                                {flashIcon}
                                {toLocaleStringIn(topAsk / (chartSecurities?.[0]?.priceMultiplier ?? 100))}
                            </span>
                        </Button>
                    )
                })()}
            </NonInteractiveTooltip>
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chart, handleQuickTrade, isEmbedded, chartSecurityKey, chartSecurities, tradeOptions, chart.dataManager.data[chartSecurityKey]?.slice(-1)?.[0]])

    const tradeLotSize = useMemo(() => tradedSecurity?.lotSize ?? security?.lotSize ?? 1, [tradedSecurity?.lotSize, security?.lotSize])

    const [quantity, setQuantity] = useState(tradeLotSize * (tradeOptions?.quantityMultiplier ?? 1))

    useEffect(() => {
        setQuantity(tradeLotSize * (tradeOptions?.quantityMultiplier ?? 1))
    }, [tradeLotSize, tradeOptions?.quantityMultiplier])

    const setQuantityInput = useCallback((value: number)=>{
        const newTradeOptions = {...tradeOptions}
        newTradeOptions.quantityMultiplier = (value??1)/(tradeLotSize ?? 1)
        setQuantity(value)
        dispatch(setTradeOptions(newTradeOptions))
    }, [tradeLotSize, tradeOptions])
    
    const quantityInput = useMemo(() => {
        if (!tradeOptions || !tradeOptions.quickTrade) return null

        return (
            <Box width={100} sx={{ height: 22, display: 'flex', alignItems: 'center' }}>
                <NumberInputField
                    value={quantity}
                    onValueChange={setQuantityInput}
                    minValue={tradeLotSize}
                    height={14}
                    stepSize={tradeLotSize}
                    sx={{ background: (theme) => theme.palette.background.paper }}
                />
            </Box>
        )
    }, [tradeOptions, quantity, tradeLotSize, setQuantityInput])

    const onSurveillanceConfirm = useCallback(() => {
        placeOrderRequestRef.current && handlePlaceOrder(placeOrderRequestRef.current)
        placeOrderRequestRef.current = null
        setShowSurveillanceDialog(false)
    }, [])

    const onSurveillanceCancel = useCallback(() => {
        placeOrderRequestRef.current = null
        setShowSurveillanceDialog(false)
    }, [])

    if (category !== SeriesGroupCategory.SERIES_DRAWING && !metadataContext[id]) return null

    return (
        <>
            <ClickAwayListener onClickAway={onClickAway}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap' }} className="series-meta-container">
                    <span
                        className="series-meta-name"
                        style={{ fontSize: '13px' }}
                        onClick={() => {
                            setActive(!active && !screenerChart)
                        }}
                    >
                        <Box
                            className={`series-meta-buttons-wrapper ${openMore || active ? 'series-meta-buttons-wrapper-clicked-state' : ''}`}
                            sx={{
                                display: screenerChart ? 'none' : 'flex',
                                backgroundColor: (theme) => theme.palette.background.paper,
                                borderColor: (theme) => (active ? theme.palette.primary.main : theme.palette.divider)
                            }}
                        >
                            {hideShowButtons}
                            {settingsButton}
                            {closeButton}
                            {seriesGroupMenu}
                        </Box>
                        {!screenerChart ? (
                            <div style={{ zIndex: 10, position: 'relative', display: 'flex', gap: 0.25 }}>
                                {primary ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row', gap: 0.25 }}>
                                        {(chartSettingsStatusLine?.showSymbolName ?? true) && !isScalperEmbedded && <span className="series-title">{nameContainer}</span>}
                                        {showOptionExpiriesMenu && <OptionsChartExpiryMenu defaultName={derivedDetails?.expiryName} />}
                                        {showOptionStrikesMenu && <OptionsChartStrikeMenu />}
                                        {showFutureExpiriesMenu && <FutureExpiryMenu defaultName={derivedDetails?.expiryName} />}
                                        {!isScalperEmbedded && (
                                            <TimeBuckets
                                                registerShortcut={false}
                                                viewKey={viewKey}
                                                key={viewKey}
                                                showFavorites={false}
                                                buttonProps={{
                                                    sx: { margin: 0, padding: 0, maxHeight: '24px', fontSize: '14px' }
                                                }}
                                            />
                                        )}
                                        {!expressionMode && (chartSettingsStatusLine?.showExchange ?? true) && security && !isScalperEmbedded && (
                                            <ExchangeMenuButton currentExchange={security.exchange} companyCode={security.companyCode} symbol={security.code} securities={[security]} />
                                        )}
                                    </Box>
                                ) : (
                                    !isScalperEmbedded && (
                                        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                            {(chartSettingsStatusLine?.indicatorTitle ?? true) && name}
                                        </Box>
                                    )
                                )}{' '}
                            </div>
                        ) : (
                            <div style={{ zIndex: 10, position: 'relative' }}>
                                {primary ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row', gap: 0.5 }}>
                                        {name}
                                        {showOptionExpiriesMenu && <OptionsChartExpiryMenu defaultName={derivedDetails?.expiryName} />}
                                        {showOptionStrikesMenu && <OptionsChartStrikeMenu />}
                                        {showFutureExpiriesMenu && <FutureExpiryMenu defaultName={derivedDetails?.expiryName} />}
                                    </Box>
                                ) : (
                                    !isScalperEmbedded && name
                                )}{' '}
                            </div>
                        )}
                    </span>
                    {alertBadge}

                    {errorContainer}

                    {category !== SeriesGroupCategory.SERIES_DRAWING ? <>{elements}</> : null}
                    <span onClick={(e) => e.stopPropagation()}>
                        {!isEmbedded && open && (
                            <SeriesGroupConfigurationPopup
                                fields={indicator.inputs}
                                id={seriesGroup.id}
                                inputValues={seriesGroup.inputValues}
                                title={indicator.name}
                                outputValues={seriesGroup.outputValues}
                                onInputsUpdate={onInputsUpdate}
                                onOutputsUpdate={onOutputsUpdate}
                                viewKey={viewKey}
                                onClose={handlePopUpClose}
                                onSubmit={handlePopUpClose}
                                security={security}
                            />
                        )}
                        {!isEmbedded && openDrawingSettings && drawingIds.length && (
                            <ChartDrawingSettingsModal
                                id={drawingIds[0]}
                                extraSettingsGroups={extraDrawingSettings}
                                onClose={() => setOpenDrawingSettings(false)}
                                onDrawingSettingGroupsUpdated={onDrawingSettingGroupUpdated}
                            />
                        )}
                    </span>
                    <Popover
                        open={Boolean(alertsAnchorEl)}
                        anchorEl={alertsAnchorEl}
                        onClose={() => setAlertsAnchorEl(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        PaperProps={{
                            sx: {
                                mt: 0.75,
                                p: '8px',
                                minWidth: 380,
                                maxWidth: 540,
                                borderRadius: '4px',
                                backgroundColor: '#1f2024',
                                boxShadow: '0px 12px 30px rgba(0, 0, 0, 0.35)',
                            }
                        }}
                    >
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                            <Box
                                sx={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: '50%',
                                    backgroundColor: 'rgba(37, 179, 158, 0.16)',
                                    color: '#25B39E',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <AlarmIcon style={{ fontSize: '24px' }} />
                            </Box>
                            <Typography sx={{ fontWeight: 700, color: '#25B39E', fontSize: '14px' }}>
                                Alerts · {groupedAlerts.length}
                            </Typography>
                        </Stack>
                        <Stack spacing={1.5}>
                            {groupedAlerts.map((alert) => (
                                <Typography key={alert.id} sx={{ fontSize: '13px', color: '#e7e7e8', lineHeight: 1.35 }}>
                                    {alert.message || alert.name || 'Alert'}
                                </Typography>
                            ))}
                        </Stack>
                    </Popover>
                </Box>
            </ClickAwayListener>
            {showSurveillanceDialog && <SurveillanceWarningDialog open={showSurveillanceDialog} info={surveillanceInfo} onClose={onSurveillanceCancel} onConfirm={onSurveillanceConfirm} />}
            {primary &&
                !screenerChart &&
                !chartExpressionMode &&
                !isScalperEmbedded &&
                !isEmbeddedStrategyChartContext() &&
                (chartTradingSettings?.showBuySellButtons ?? true) &&
                chartSecurities?.[0]?.categoryCode !== SecurityCategory_CategoryCode.INDEX &&
                (tradedSecurity && tradedSecurity?.expiry?.expiry ? !isDatePassed(tradedSecurity.expiry.expiry) : true) ? (
                <Stack
                    direction={'row'}
                    gap={0.5}
                    className="series-meta-container"
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center'
                    }}
                >
                    {sellMarketContainer}
                    {tradeOptions?.quickTrade ? quantityInput : spreadContainer}
                    {buyMarketContainer}
                </Stack>
            ) : null}
        </>
    )
}
export default memo(SeriesMetadataDisplay)
